import { computed, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import {
  debounceTime,
  EMPTY,
  exhaustMap,
  map,
  Observable,
  of,
  pipe,
  switchMap,
  tap,
  toArray,
} from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import {
  hasInactivationCondition,
  INACTIVATION_PREVIEW_PERSONS_MAX_TOP,
  isSameInactivationRule,
  isValidInactivationRule,
  TInactivationPreview,
  TInactivationPreviewPersons,
  TInactivationPreviewPersonsRequest,
  TInactivationPreviewScope,
  TInactivationRule,
  TInactivationRuleInput,
  TInactivationRuleReview,
  toInactivationRuleInput,
} from '../entities/inactivation-rule.model';
import { InactivationRuleService } from '../infra/inactivation-rule.service';
import { fetchAllPages } from './fetch-all-pages.util';

/** Pause after the last change before the preview is requested. */
export const INACTIVATION_PREVIEW_DEBOUNCE_MS = 400;

/** People per page of a preview group on the screen (the http contract's 200). */
export const INACTIVATION_PREVIEW_PERSONS_PAGE_SIZE = 200;

/** A page of the preview people: the scope plus the window. */
export type TInactivationPreviewPersonsQuery = TInactivationPreviewScope & {
  Skip: number;
  /** Defaults to {@link INACTIVATION_PREVIEW_PERSONS_PAGE_SIZE}; capped at 1000. */
  Top?: number;
};

/** How far an export walk got. */
export type TInactivationExportProgress = {
  Fetched: number;
  Total: number;
};

type TInactivationRuleState = {
  /** The version in force (`GET /InactivationRule`). */
  rule: TInactivationRule | null;
  /** The cutover review (`GET /InactivationRule/Review`). */
  review: TInactivationRuleReview | null;
  /** The last rule handed to `previewDraft` — what the card is editing. */
  draft: TInactivationRuleInput | null;
  /** The last preview that arrived. */
  preview: TInactivationPreview | null;
  /** The rule `preview` was computed for. */
  previewRule: TInactivationRuleInput | null;
  /** The page of people on screen, and the query that produced it. */
  persons: TInactivationPreviewPersons | null;
  personsQuery: TInactivationPreviewPersonsQuery | null;
  /** Every person of a scope, for the export. */
  exportScope: TInactivationPreviewScope | null;
  exportedPersons: TInactivationPreviewPersons | null;
  exportProgress: TInactivationExportProgress | null;
  /** The last save was refused with 409 (someone saved first, or the review is done). */
  saveConflict: boolean;
  /** The cutover modal was closed for this session; "Revisar agora" opens it again. */
  reviewDismissed: boolean;
  /** "Ajustar regras" asked the rules card to open in editing. */
  editRequested: boolean;
};

const initialState: TInactivationRuleState = {
  rule: null,
  review: null,
  draft: null,
  preview: null,
  previewRule: null,
  persons: null,
  personsQuery: null,
  exportScope: null,
  exportedPersons: null,
  exportProgress: null,
  saveConflict: false,
  reviewDismissed: false,
  editRequested: false,
};

const OPS = [
  'load',
  'loadReview',
  'preview',
  'persons',
  'exportPersons',
  'save',
] as const;

const ERRORS = {
  load: 'SETTINGS.INACTIVATION_RULES.ERRORS.LOAD',
  loadReview: 'SETTINGS.INACTIVATION_RULES.ERRORS.LOAD_REVIEW',
  preview: 'SETTINGS.INACTIVATION_RULES.ERRORS.PREVIEW',
  persons: 'SETTINGS.INACTIVATION_RULES.ERRORS.PERSONS',
  exportPersons: 'SETTINGS.INACTIVATION_RULES.ERRORS.EXPORT',
  save: 'SETTINGS.INACTIVATION_RULES.ERRORS.SAVE',
} as const;

/**
 * A rule the backend can simulate: valid (same checks as `Validate`) and with
 * at least one condition. A rule with no condition is never sent, not even
 * switched off: with nothing to meet, the evaluator has nothing to say.
 */
function isPreviewable(rule: TInactivationRuleInput): boolean {
  return isValidInactivationRule(rule) && hasInactivationCondition(rule);
}

/** One request of the preview people, scope normalized: a group wins over an outcome. */
function personsRequest(
  rule: TInactivationRuleInput,
  scope: TInactivationPreviewScope,
  computedOnUtc: string,
  skip: number,
  top: number
): TInactivationPreviewPersonsRequest {
  const groupKey = scope.GroupKey ?? '';
  return {
    Rule: rule,
    GroupKey: groupKey,
    Outcome: groupKey ? null : scope.Outcome ?? null,
    ComputedOnUtc: computedOnUtc,
    Skip: skip,
    Top: top,
  };
}

/** The pages of one export as a single answer. */
function joinPages(
  pages: TInactivationPreviewPersons[]
): TInactivationPreviewPersons | null {
  const last = pages[pages.length - 1];
  if (!last) {
    return null;
  }
  return {
    Total: last.Total,
    Items: pages.flatMap((page) => page.Items),
    ComputedOnUtc: last.ComputedOnUtc,
    Recomputed: pages.some((page) => page.Recomputed),
  };
}

/**
 * Ajustes › Clientes e Visitantes › Inativação automática: the rule in force,
 * the live preview of the rule being edited, the people of each group, the
 * export of a whole scope, the cutover review and the save.
 *
 * Errors: each op's `*Error` holds the backend message, or an i18n fallback
 * key when there is none (render it through `translate`). Only `save` raises
 * the global alert by itself; loads, preview, people and export leave the
 * error to the screen's own state.
 */
export const InactivationRuleStore = signalStore(
  { providedIn: 'root' },
  withState<TInactivationRuleState>(initialState),
  withReqState(...OPS),
  withComputed((store) => ({
    isEnabled: computed<boolean>(() => store.rule()?.IsEnabled ?? false),
    reviewPending: computed<boolean>(() => store.review()?.Pending ?? false),
    /** The draft can be simulated (valid and with at least one condition). */
    draftIsPreviewable: computed<boolean>(() => {
      const draft = store.draft();
      return !!draft && isPreviewable(draft);
    }),
    /** The preview on screen is the one of the rule being edited. */
    previewIsCurrent: computed<boolean>(
      () =>
        !!store.preview() &&
        isSameInactivationRule(store.draft(), store.previewRule())
    ),
    /** The number the save confirmation shows, when the preview is current. */
    wouldInactivate: computed<number | null>(() =>
      store.preview() &&
      isSameInactivationRule(store.draft(), store.previewRule())
        ? store.preview()?.WouldInactivate ?? null
        : null
    ),
    /**
     * "Salvar regras" may be pressed: a valid draft (an enabled rule needs a
     * condition), no preview being computed, no save in flight.
     */
    canSave: computed<boolean>(() => {
      const draft = store.draft();
      return (
        !!draft &&
        isValidInactivationRule(draft) &&
        !store.previewLoading() &&
        !store.saveLoading()
      );
    }),
  })),
  withMethods(
    (
      store,
      service = inject(InactivationRuleService),
      errorStore = inject(ErrorStore)
    ) => {
      const messageOf = (error: HttpErrorResponse, fallbackKey: string) =>
        errorStore.getHttpErrorMessage(error) ?? fallbackKey;

      const reviewForSave = (): Observable<TInactivationRuleReview> => {
        const loaded = store.review();
        if (loaded) {
          return of(loaded);
        }
        return service
          .getReview()
          .pipe(
            tap((review) =>
              patchState(store, { review }, setPropLoaded('loadReview'))
            )
          );
      };

      return {
        /** Loads the version in force. */
        load: rxMethod<void>(
          pipe(
            tap(() => patchState(store, setPropLoading('load'))),
            switchMap(() =>
              service.getCurrent().pipe(
                tapResponse({
                  next: (rule) =>
                    patchState(store, { rule }, setPropLoaded('load')),
                  error: (error: HttpErrorResponse) =>
                    patchState(
                      store,
                      setPropError('load', messageOf(error, ERRORS.load))
                    ),
                })
              )
            )
          )
        ),

        /** Loads the cutover review. 403 without `RegrasInativacaoAlterar`. */
        loadReview: rxMethod<void>(
          pipe(
            tap(() => patchState(store, setPropLoading('loadReview'))),
            switchMap(() =>
              service.getReview().pipe(
                tapResponse({
                  next: (review) =>
                    patchState(store, { review }, setPropLoaded('loadReview')),
                  error: (error: HttpErrorResponse) =>
                    patchState(
                      store,
                      setPropError(
                        'loadReview',
                        messageOf(error, ERRORS.loadReview)
                      )
                    ),
                })
              )
            )
          )
        ),

        /**
         * Hands the rule being edited to the preview. Call it on every change
         * (or bind a signal / observable of the form value): the store waits
         * {@link INACTIVATION_PREVIEW_DEBOUNCE_MS} after the last change and
         * cancels a request a newer change made obsolete.
         *
         * `previewLoading` turns on at the change itself, not after the pause,
         * so "Salvar regras" stays disabled for the whole gap. A rule that
         * cannot be simulated (invalid, or no condition) clears the preview
         * and is never sent.
         */
        previewDraft: rxMethod<TInactivationRuleInput>(
          pipe(
            map(toInactivationRuleInput),
            tap((rule) =>
              isPreviewable(rule)
                ? patchState(store, { draft: rule }, setPropLoading('preview'))
                : patchState(
                    store,
                    { draft: rule, preview: null, previewRule: null },
                    setPropInit('preview')
                  )
            ),
            debounceTime(INACTIVATION_PREVIEW_DEBOUNCE_MS),
            switchMap((rule) =>
              !isPreviewable(rule)
                ? EMPTY
                : service.preview(rule).pipe(
                    tapResponse({
                      next: (preview) => {
                        // A newer draft is on its way: this answer is stale.
                        if (!isSameInactivationRule(store.draft(), rule)) {
                          return;
                        }
                        patchState(
                          store,
                          {
                            preview,
                            previewRule: rule,
                            persons: null,
                            personsQuery: null,
                          },
                          setPropLoaded('preview')
                        );
                      },
                      error: (error: HttpErrorResponse) => {
                        if (!isSameInactivationRule(store.draft(), rule)) {
                          return;
                        }
                        patchState(
                          store,
                          setPropError(
                            'preview',
                            messageOf(error, ERRORS.preview)
                          )
                        );
                      },
                    })
                  )
            )
          )
        ),

        /**
         * One page of people of the preview on screen — a group, an outcome or
         * everyone — from the same simulation (`ComputedOnUtc`). Does nothing
         * while there is no preview.
         */
        loadPersons: rxMethod<TInactivationPreviewPersonsQuery>(
          pipe(
            switchMap((query) => {
              const rule = store.previewRule();
              const preview = store.preview();
              if (!rule || !preview) {
                return EMPTY;
              }
              const top = Math.min(
                query.Top ?? INACTIVATION_PREVIEW_PERSONS_PAGE_SIZE,
                INACTIVATION_PREVIEW_PERSONS_MAX_TOP
              );
              patchState(
                store,
                { personsQuery: query },
                setPropLoading('persons')
              );
              return service
                .previewPersons(
                  personsRequest(
                    rule,
                    query,
                    preview.ComputedOnUtc,
                    query.Skip,
                    top
                  )
                )
                .pipe(
                  tapResponse({
                    next: (persons) =>
                      patchState(store, { persons }, setPropLoaded('persons')),
                    error: (error: HttpErrorResponse) =>
                      patchState(
                        store,
                        setPropError(
                          'persons',
                          messageOf(error, ERRORS.persons)
                        )
                      ),
                  })
                );
            })
          )
        ),

        /**
         * EVERY person of a scope, for the export: pages of 1000 until
         * `Total`, each citing the simulation of the page before. The result
         * lands in `exportedPersons` (`Recomputed` if any page came from a newer
         * simulation); `exportProgress` follows the walk. Does nothing while
         * there is no preview.
         */
        exportPersons: rxMethod<TInactivationPreviewScope>(
          pipe(
            switchMap((scope) => {
              const rule = store.previewRule();
              const preview = store.preview();
              if (!rule || !preview) {
                return EMPTY;
              }
              patchState(
                store,
                {
                  exportScope: scope,
                  exportedPersons: null,
                  exportProgress: { Fetched: 0, Total: 0 },
                },
                setPropLoading('exportPersons')
              );
              return fetchAllPages<TInactivationPreviewPersons>(
                (skip, previous) =>
                  service.previewPersons(
                    personsRequest(
                      rule,
                      scope,
                      previous?.ComputedOnUtc ?? preview.ComputedOnUtc,
                      skip,
                      INACTIVATION_PREVIEW_PERSONS_MAX_TOP
                    )
                  )
              ).pipe(
                tap((page) =>
                  patchState(store, (state) => ({
                    exportProgress: {
                      Fetched:
                        (state.exportProgress?.Fetched ?? 0) +
                        page.Items.length,
                      Total: page.Total,
                    },
                  }))
                ),
                toArray(),
                tapResponse({
                  next: (pages) =>
                    patchState(
                      store,
                      { exportedPersons: joinPages(pages) },
                      setPropLoaded('exportPersons')
                    ),
                  error: (error: HttpErrorResponse) =>
                    patchState(
                      store,
                      setPropError(
                        'exportPersons',
                        messageOf(error, ERRORS.exportPersons)
                      )
                    ),
                })
              );
            })
          )
        ),

        /**
         * Saves the rule. With the cutover review pending it CONFIRMS the
         * review (`POST /InactivationRule/Review`); otherwise it saves a new
         * version (`POST /InactivationRule`). When the review was never
         * loaded it is fetched first, so a pending review is never skipped.
         *
         * Switching the rule off is a save with `IsEnabled = false`. A second
         * call while one is in flight is ignored (no double version). Refusals
         * raise the global alert; a 409 also sets `saveConflict` — reload.
         */
        save: rxMethod<TInactivationRuleInput>(
          pipe(
            map(toInactivationRuleInput),
            exhaustMap((rule) => {
              patchState(
                store,
                { saveConflict: false },
                setPropLoading('save')
              );
              return reviewForSave().pipe(
                switchMap((review) =>
                  review.Pending
                    ? service
                        .confirmReview(rule)
                        .pipe(map((saved) => ({ saved, reviewed: true })))
                    : service
                        .save(rule)
                        .pipe(map((saved) => ({ saved, reviewed: false })))
                ),
                tapResponse({
                  next: ({ saved, reviewed }) =>
                    patchState(
                      store,
                      (state) => ({
                        rule: saved,
                        review:
                          reviewed && state.review
                            ? {
                                ...state.review,
                                Pending: false,
                                Current: saved,
                              }
                            : state.review,
                      }),
                      setPropLoaded('save')
                    ),
                  error: (error: HttpErrorResponse) => {
                    patchState(
                      store,
                      { saveConflict: error?.status === 409 },
                      setPropError('save', messageOf(error, ERRORS.save))
                    );
                    errorStore.setHttpError(error);
                  },
                })
              );
            })
          )
        ),

        /** Drops the page of people (closing a group). */
        clearPersons: (): void =>
          patchState(
            store,
            { persons: null, personsQuery: null },
            setPropInit('persons')
          ),

        /** Drops the exported rows once the file is written. */
        clearExport: (): void =>
          patchState(
            store,
            { exportScope: null, exportedPersons: null, exportProgress: null },
            setPropInit('exportPersons')
          ),

        /** Closes the cutover modal for this session. */
        dismissReview: (): void => patchState(store, { reviewDismissed: true }),

        /** "Revisar agora": shows the cutover modal again. */
        reopenReview: (): void => patchState(store, { reviewDismissed: false }),

        /**
         * "Ajustar regras": the rules card opens in editing. It goes through the
         * store because the card may already be on screen, and navigating to
         * the page it is on does nothing.
         */
        requestEdit: (): void => patchState(store, { editRequested: true }),

        /** The card took the edit request. */
        consumeEditRequest: (): void =>
          patchState(store, { editRequested: false }),

        /**
         * Back to the untouched state (leaving the page). A closed cutover modal
         * stays closed: the card reloads the review on its next visit, and the
         * modal must not pop over the page again.
         */
        reset: (): void =>
          patchState(
            store,
            (state) => ({
              ...initialState,
              reviewDismissed: state.reviewDismissed,
            }),
            setPropInit(...OPS)
          ),
      };
    }
  )
);
